import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { EllipseName } from 'lib_ts/enums/ellipses';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { IEllipse } from 'lib_ts/interfaces/i-ellipses';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IAimedMsMsgResult } from 'lib_ts/interfaces/i-pitch-aiming';
import { IPitch, IPlateLoc } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clone } from 'lodash';
import { SessionEventsService } from 'services/session-events.service';

// Flag to enable/disable assertion checks for aimed plate precision
const CHECK_ASSERTS = true;

/**
 * Helper class for aiming context operations, including safety event detection and ad-hoc aiming logic.
 */
export class AimingContextHelper {
  /**
   * Detects if a shot is outside all ellipses or within a specific confidence ellipse, and logs safety events accordingly.
   *
   * @param newShot - The machine shot to evaluate.
   * @param ellipse - The reference ellipse for containment checks.
   * @param target - The intended plate location.
   */
  static detectSafetyEvent = (
    newShot: IMachineShot,
    ellipse: IEllipse,
    target: IPlateLoc
  ) => {
    // Get the actual plate location from the shot's trajectory
    const actual: IPlateLoc = TrajHelper.getPlateLoc(newShot.traj);
    // Determine if the actual location is within the provided ellipse
    const actualEllipse = EllipseHelper.getEllipseContainingPoint({
      origin: target,
      point: actual,
      ellipse: ellipse,
      isPointRotated: true,
    });

    // If not contained in any ellipse, log a safety event
    if (!actualEllipse) {
      SessionEventsService.createOne({
        category: 'safety',
        tags: 'ellipse',
        data: {
          event: 'shot detected outside of all ellipses',
          target: target,
          actual: actual,
          unscaledEllipse: ellipse,
        },
      });
      return;
    }

    // If contained within the 95% confidence ellipse, log a specific event
    if (actualEllipse.name === EllipseName.Conf95) {
      SessionEventsService.createOne({
        category: 'safety',
        tags: 'ellipse',
        data: {
          event: `shot detected within ${actualEllipse.name} ellipse`,
          target: target,
          actual: actual,
          unscaledEllipse: ellipse,
          foundIn: actualEllipse.name,
          scaling: actualEllipse.factor,
        },
      });
      return;
    }
  };

  // ! deprecate this if we deprecate edit spins/breaks
  /**
   * Computes an aimed result for a given pitch, machine, and state, using provided shots.
   * Performs assertions to ensure the aimed plate matches the target plate within a precision threshold.
   *
   * @remarks
   * This method may be deprecated if a more general aiming function is confirmed to be sufficient.
   *
   * @param config.source - Source identifier for logging.
   * @param config.machine - The machine configuration.
   * @param config.pitch - The pitch to aim.
   * @param config.ms - The machine state.
   * @param config.plate - The target plate location.
   * @param config.usingShots - Array of shots to use for aiming.
   * @returns The aimed result, or undefined if input data is invalid.
   */
  static aimAdHoc(config: {
    source: string;
    machine: IMachine;
    pitch: IPitch;
    ms: IMachineState;
    plate: IPlateLoc;
    aimingShots: IMachineShot[];
  }): IAimedMsMsgResult | undefined {
    const FN_NAME = `${config.source} > aimAdHoc`;
    const PLATE_PRECISION_FT = 0.01;

    // Validate required pitch and state data
    if (!config.pitch.bs || !config.pitch.traj || !config.ms) {
      /** Should not trigger, but will be filtered out to be safe */
      console.warn({
        event: `${FN_NAME} > invalid data detected`,
        pitch: config.pitch,
        invalid_bs: !config.pitch.bs,
        invalid_traj: !config.pitch.traj,
        invalid_ms: !config.ms,
      });
      // Optionally notify user via UI
      // NotifyHelper.warning({
      //   message_md: `Data for pitch "${config.pitch.name}" is invalid. Please see console for more info.`,
      //   delay_ms: 0,
      // });
      return;
    }

    // Perform aiming calculation using provided helpers
    const aimed = AimingHelper.aimWithShots({
      bs: config.pitch.bs,
      ms: config.ms,
      traj: config.pitch.traj,

      seams: config.pitch.seams,
      breaks: config.pitch.breaks,
      priority: config.pitch.priority ?? BuildPriority.Spins,
      targetPlate: config.plate,
      releaseFt: config.machine.releaseFt,
      aimingShots: config.aimingShots,
      stepSize: undefined,
      clampTilt: false,
      checkConfidence: false,
      training: false,
    });

    if (!aimed) {
      return undefined;
    }

    // Assert that the aimed plate matches the target plate within precision
    if (CHECK_ASSERTS) {
      const aimedPlate = TrajHelper.getPlateLoc(aimed.traj);

      console.assert(
        Math.abs(aimedPlate.plate_x - config.plate.plate_x) <
          PLATE_PRECISION_FT,
        `large plate_x delta (${(
          aimedPlate.plate_x - config.plate.plate_x
        ).toFixed(4)}) between rotated traj plate (${aimedPlate.plate_x.toFixed(
          4
        )}) and target plate (${config.plate.plate_x.toFixed(4)})`
      );

      console.assert(
        Math.abs(aimedPlate.plate_z - config.plate.plate_z) <
          PLATE_PRECISION_FT,
        `large plate_z delta (${(
          aimedPlate.plate_z - config.plate.plate_z
        ).toFixed(4)}) between rotated traj plate (${aimedPlate.plate_z.toFixed(
          4
        )}) and target plate (${config.plate.plate_z.toFixed(4)})`
      );
    }

    // Clone the pitch and update with aimed results
    const outputPitch = clone(config.pitch);

    outputPitch.bs = aimed.bs;
    outputPitch.bs_hash = MachineHelper.getBsHash(
      aimed.bs,
      config.pitch.priority
    );
    outputPitch.traj = aimed.traj;

    // Build the aimed result object
    const output: IAimedMsMsgResult = {
      pitch: {
        ...outputPitch,
        _mx_ms: aimed.ms,
      },
      plate: config.plate,
      usingShots: aimed.usingShots,
      usingTraj: aimed.usingTraj,
      aimingTraj: aimed.aimingTraj,
      msg: undefined,
      msgHash: undefined,
      shotsRequired: true,
    };

    return output;
  }
}
